import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { HomeIcon, UsersIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import api from '../../api/client';

export default function JoinFamily() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoin = async () => {
    if (!code) return;
    setIsJoining(true);
    setError(null);
    try {
      await api.post('/families/join', { invite_code: code });
      toast.success('You have joined the family!');
      navigate('/families');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to join family');
    } finally {
      setIsJoining(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-[100px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-[100px] animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-3xl p-8 text-center">
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
            <UsersIcon className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">
            Family Invitation
          </h1>
          <p className="text-surface-400 mb-8">
            You've been invited to join a family on MyFamily
          </p>

          {isAuthenticated ? (
            <div className="space-y-4">
              {error && (
                <div className="p-4 rounded-xl bg-red-500/20 border border-red-500/20 text-red-300 text-sm">
                  {error}
                </div>
              )}
              <button
                onClick={handleJoin}
                disabled={isJoining}
                className="btn-primary w-full"
              >
                {isJoining ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Joining...</span>
                  </div>
                ) : (
                  <>
                    <span>Accept Invitation</span>
                    <ArrowRightIcon className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-surface-300">
                Please sign in or create an account to join
              </p>
              <div className="flex gap-3">
                <Link
                  to={`/login?redirect=/join/${code}`}
                  className="btn-secondary flex-1"
                >
                  Sign In
                </Link>
                <Link
                  to={`/register?redirect=/join/${code}`}
                  className="btn-primary flex-1"
                >
                  Register
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Back to home */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-surface-400 hover:text-white transition-colors"
          >
            <HomeIcon className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

